/**
 * NdeImportModal - Import ISO sheets from piping tracker into NDE tracker
 * 
 * Shows ALL unique fluid codes from the progress tracker.
 * User checks which fluid codes to import, sets NDE type and required %.
 * Defaults: >2" = X-Ray, <=2" = LPI.
 */

import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';

const NdeImportModal = ({ isOpen, onClose, projectId, jobNumber, onImportSuccess, onImportError }) => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [fluidCodes, setFluidCodes] = useState({});
  const [importedByFc, setImportedByFc] = useState({});
  const [checkedFcs, setCheckedFcs] = useState({});
  const [fcSettings, setFcSettings] = useState({});
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [manualRecord, setManualRecord] = useState({
    iso_sheet_number: '', line_number: '', fluid_code: '',
    welder_number: '', nde_type: 'X-Ray', qty_joint: 0, qty_nde: 0, qty_failed: 0
  });

  useEffect(() => {
    if (isOpen) fetchAvailableIsos();
  }, [isOpen]);

  const fetchAvailableIsos = async () => {
    try {
      setLoading(true);
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/nde/available-isos/${projectId}`);
      if (response.data.success) {
        const fcs = response.data.data.fluidCodes || {};
        setFluidCodes(fcs);
        setImportedByFc(response.data.data.importedByFluidCode || {});

        const settings = {};
        const checked = {};
        Object.keys(fcs).forEach(fc => {
          const sizes = fcs[fc].sizes || [];
          const hasLargeSize = sizes.some(s => parseFloat(s) > 2);
          settings[fc] = { ndeType: hasLargeSize ? 'X-Ray' : 'LPI', percentage: 5 };
          checked[fc] = false;
        });
        setFcSettings(settings);
        setCheckedFcs(checked);
      }
    } catch (error) {
      console.error('Error fetching available ISOs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    const selectedSettings = {};
    Object.keys(checkedFcs).forEach(fc => {
      if (checkedFcs[fc]) {
        selectedSettings[fc] = fcSettings[fc];
      }
    });

    if (Object.keys(selectedSettings).length === 0) return;

    try {
      setImporting(true);
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/nde/import-from-tracker', {
        projectId,
        fluidCodePercentages: selectedSettings,
      });
      if (response.data.success) {
        onImportSuccess && onImportSuccess();
      } else {
        onImportError && onImportError(response.data.message);
      }
    } catch (error) {
      onImportError && onImportError(error.message);
    } finally {
      setImporting(false);
    }
  };

  const handleManualAdd = async () => {
    try {
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/nde/add-record', {
        projectId,
        record: manualRecord,
      });
      if (response.data.success) {
        setManualRecord({
          iso_sheet_number: '', line_number: '', fluid_code: '',
          welder_number: '', nde_type: 'X-Ray', qty_joint: 0, qty_nde: 0, qty_failed: 0
        });
        setShowManualAdd(false);
        onImportSuccess && onImportSuccess();
      }
    } catch (error) {
      onImportError && onImportError(error.message);
    }
  };

  const handleToggleAll = (val) => {
    const updated = {};
    Object.keys(checkedFcs).forEach(fc => { updated[fc] = val; });
    setCheckedFcs(updated);
  };

  if (!isOpen) return null;

  const checkedCount = Object.values(checkedFcs).filter(Boolean).length;
  const totalIsoCount = Object.keys(checkedFcs)
    .filter(fc => checkedFcs[fc])
    .reduce((sum, fc) => sum + (fluidCodes[fc]?.isoCount || 0), 0);

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px', width: '750px', maxHeight: '85vh',
        overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', color: 'white',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px' }}>Import NDE ISOs</h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', opacity: 0.9 }}>
              Select fluid codes to import from Progress Tracker
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer',
          }}>&times;</button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>Loading fluid codes...</div>
          ) : (
            <>
              {/* Fluid Code Selection Table */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600' }}>
                  Fluid Codes ({Object.keys(fluidCodes).length} available)
                </h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => handleToggleAll(true)} style={{ padding: '4px 10px', fontSize: '12px', backgroundColor: '#dbeafe', border: '1px solid #93c5fd', borderRadius: '4px', cursor: 'pointer' }}>
                    Select All
                  </button>
                  <button onClick={() => handleToggleAll(false)} style={{ padding: '4px 10px', fontSize: '12px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}>
                    Clear All
                  </button>
                </div>
              </div>

              {Object.keys(fluidCodes).length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                  No fluid codes found in the Progress Tracker for this project.
                </div>
              ) : (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', marginBottom: '16px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8fafc' }}>
                        <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', width: '40px' }}></th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Fluid Code</th>
                        <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>ISOs</th>
                        <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Imported</th>
                        <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>NDE Type</th>
                        <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Required %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(fluidCodes).map(([fc, data]) => (
                        <tr key={fc} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: checkedFcs[fc] ? '#f0f9ff' : '#fff' }}>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={checkedFcs[fc] || false}
                              onChange={(e) => setCheckedFcs(prev => ({ ...prev, [fc]: e.target.checked }))}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                          </td>
                          <td style={{ padding: '8px 12px', fontWeight: '500' }}>{fc}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>{data.isoCount}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'center', color: '#6b7280' }}>{importedByFc[fc] || 0}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                            <select
                              value={fcSettings[fc]?.ndeType || 'X-Ray'}
                              onChange={(e) => setFcSettings(prev => ({
                                ...prev, [fc]: { ...prev[fc], ndeType: e.target.value }
                              }))}
                              disabled={!checkedFcs[fc]}
                              style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '13px', opacity: checkedFcs[fc] ? 1 : 0.5 }}
                            >
                              <option value="X-Ray">X-Ray</option>
                              <option value="LPI">LPI</option>
                              <option value="MPI">MPI</option>
                              <option value="UT">UT</option>
                              <option value="VT">VT</option>
                              <option value="PMI">PMI</option>
                            </select>
                          </td>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={fcSettings[fc]?.percentage || 5}
                              onChange={(e) => setFcSettings(prev => ({
                                ...prev, [fc]: { ...prev[fc], percentage: parseInt(e.target.value) || 0 }
                              }))}
                              disabled={!checkedFcs[fc]}
                              style={{ width: '60px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '13px', textAlign: 'center', opacity: checkedFcs[fc] ? 1 : 0.5 }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Summary */}
              {checkedCount > 0 && (
                <div style={{ padding: '10px 14px', backgroundColor: '#f0fdf4', borderRadius: '6px', marginBottom: '16px', fontSize: '13px', color: '#166534' }}>
                  {checkedCount} fluid code{checkedCount > 1 ? 's' : ''} selected ({totalIsoCount} ISOs will be imported)
                </div>
              )}

              {/* Manual Add Section */}
              <div style={{ marginTop: '8px' }}>
                <button
                  onClick={() => setShowManualAdd(!showManualAdd)}
                  style={{
                    padding: '8px 16px', backgroundColor: '#f8fafc', border: '1px solid #d1d5db',
                    borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
                  }}
                >
                  {showManualAdd ? '- Hide Manual Add' : '+ Manually Add ISO/Line'}
                </button>

                {showManualAdd && (
                  <div style={{ marginTop: '12px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>ISO Sheet #</label>
                        <input
                          value={manualRecord.iso_sheet_number}
                          onChange={(e) => setManualRecord(prev => ({ ...prev, iso_sheet_number: e.target.value }))}
                          style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #d1d5db', marginTop: '4px', fontSize: '13px' }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>Line Number</label>
                        <input
                          value={manualRecord.line_number}
                          onChange={(e) => setManualRecord(prev => ({ ...prev, line_number: e.target.value }))}
                          style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #d1d5db', marginTop: '4px', fontSize: '13px' }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>Fluid Code</label>
                        <input
                          value={manualRecord.fluid_code}
                          onChange={(e) => setManualRecord(prev => ({ ...prev, fluid_code: e.target.value }))}
                          style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #d1d5db', marginTop: '4px', fontSize: '13px' }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>NDE Type</label>
                        <select
                          value={manualRecord.nde_type}
                          onChange={(e) => setManualRecord(prev => ({ ...prev, nde_type: e.target.value }))}
                          style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #d1d5db', marginTop: '4px', fontSize: '13px' }}
                        >
                          <option value="X-Ray">X-Ray</option>
                          <option value="LPI">LPI</option>
                          <option value="MPI">MPI</option>
                          <option value="UT">UT</option>
                          <option value="VT">VT</option>
                          <option value="PMI">PMI</option>
                        </select>
                      </div>
                    </div>
                    <button
                      onClick={handleManualAdd}
                      disabled={!manualRecord.iso_sheet_number}
                      style={{
                        marginTop: '12px', padding: '8px 16px',
                        backgroundColor: !manualRecord.iso_sheet_number ? '#93c5fd' : '#2563eb',
                        color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
                      }}
                    >
                      Add Record
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Excel Import Section */}
        <div style={{ padding: '0 24px 12px', borderTop: '1px solid #f3f4f6', marginTop: '8px', paddingTop: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>Or import from Excel:</span>
            <label style={{
              padding: '6px 14px', backgroundColor: '#7c3aed', color: 'white', border: 'none',
              borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
            }}>
              Upload Excel
              <input type="file" accept=".xlsx,.xls" onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                try {
                  const formData = new FormData();
                  formData.append('file', file);
                  formData.append('projectId', projectId);
                  const response = await authenticatedApiCall(instance, accounts, 'POST', '/nde/import-excel', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                  if (response.data.success) {
                    onImportSuccess && onImportSuccess();
                  } else {
                    onImportError && onImportError(response.data.message);
                  }
                } catch (err) {
                  onImportError && onImportError(err.message || 'Excel import failed');
                }
                e.target.value = '';
              }} style={{ display: 'none' }} />
            </label>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'flex-end', gap: '12px',
        }}>
          <button onClick={onClose} style={{
            padding: '8px 20px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db',
            borderRadius: '6px', cursor: 'pointer', fontSize: '14px',
          }}>
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={importing || checkedCount === 0}
            style={{
              padding: '8px 20px', backgroundColor: importing || checkedCount === 0 ? '#93c5fd' : '#2563eb',
              color: 'white', border: 'none', borderRadius: '6px', cursor: importing ? 'default' : 'pointer',
              fontSize: '14px', fontWeight: '500',
            }}
          >
            {importing ? 'Importing...' : `Import ${checkedCount > 0 ? totalIsoCount + ' ISOs' : '(select fluid codes)'}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NdeImportModal;
